import React, { useState, useEffect, useCallback } from 'react';
import { 
  Mail, Send, Eye, MousePointerClick, AlertTriangle, TrendingUp, 
  RefreshCw, Loader2, Calendar, ChevronDown, ChevronRight,
  BarChart3, ArrowUpRight, ArrowDownRight, Users, Ban
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios, { API } from '@/utils/api';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const EmailAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState('30days');
  const [selectedTemplate, setSelectedTemplate] = useState('all');
  const [templateStats, setTemplateStats] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email/analytics`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setAnalytics(res.data);
      }
      
      // Also fetch per-template stats
      const templateRes = await axios.get(`${API}/admin/email/template-stats`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      if (templateRes.data.success) {
        setTemplateStats(templateRes.data.templates || []);
      }
    } catch (err) {
      console.error('Error fetching email analytics:', err);
      toast.error('Failed to load email analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const COLORS = ['#df1283', '#0FFFE2', '#FF7901', '#22C55E', '#8B5CF6', '#F59E0B'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const dailyStats = analytics?.daily_stats || [];
  const campaigns = analytics?.campaigns || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-6 h-6 text-[#df1283]" />
          <h2 className="text-xl font-bold text-white">Email Analytics</h2>
          <span className="px-2 py-0.5 bg-[#df1283]/20 text-[#df1283] text-xs rounded-full">
            Advanced
          </span>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-[#4A0A66]/50 border border-[#4A0A66] text-white text-sm rounded-lg px-3 py-1.5"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
          <Button
            onClick={fetchAnalytics}
            variant="ghost"
            size="sm"
            className="text-[#FDF2F8]/60 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Send className="w-4 h-4 text-[#0FFFE2]" />
            <span className="text-xs text-[#FDF2F8]/60">Total Sent</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_sent?.toLocaleString() || 0}</p>
        </div>

        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-[#22C55E]" />
            <span className="text-xs text-[#FDF2F8]/60">Opened</span>
          </div>
          <p className="text-2xl font-bold text-[#22C55E]">{summary.total_opens?.toLocaleString() || 0}</p>
          <p className="text-xs text-[#FDF2F8]/40 mt-1">
            {summary.open_rate || 0}% rate
          </p>
        </div>

        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <MousePointerClick className="w-4 h-4 text-[#df1283]" />
            <span className="text-xs text-[#FDF2F8]/60">Clicked</span>
          </div>
          <p className="text-2xl font-bold text-[#df1283]">{summary.total_clicks?.toLocaleString() || 0}</p>
          <p className="text-xs text-[#FDF2F8]/40 mt-1">
            {summary.click_rate || 0}% rate
          </p>
        </div>

        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Ban className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-xs text-[#FDF2F8]/60">Unsubscribed</span>
          </div>
          <p className="text-2xl font-bold text-[#F59E0B]">{summary.total_unsubscribes?.toLocaleString() || 0}</p>
        </div>

        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-xs text-[#FDF2F8]/60">Bounced</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{summary.total_bounces?.toLocaleString() || 0}</p>
          <p className="text-xs text-[#FDF2F8]/40 mt-1">
            {summary.bounce_rate || 0}% rate
          </p>
        </div>

        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-[#8B5CF6]" />
            <span className="text-xs text-[#FDF2F8]/60">Campaigns</span>
          </div>
          <p className="text-2xl font-bold text-[#8B5CF6]">{summary.total_campaigns || 0}</p>
        </div>
      </div>

      {/* Performance Rates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-6">
          <h3 className="text-sm font-medium text-[#FDF2F8]/60 mb-4">Open Rate</h3>
          <div className="flex items-end gap-4">
            <span className="text-4xl font-bold text-[#22C55E]">{summary.open_rate || 0}%</span>
            <div className="flex items-center gap-1 text-xs text-[#22C55E]">
              <ArrowUpRight className="w-4 h-4" />
              <span>Industry avg: 21%</span>
            </div>
          </div>
          <div className="mt-4 h-2 bg-[#4A0A66]/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#22C55E] to-[#4ADE80] rounded-full transition-all duration-500"
              style={{ width: `${Math.min(summary.open_rate || 0, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-6">
          <h3 className="text-sm font-medium text-[#FDF2F8]/60 mb-4">Click Rate</h3>
          <div className="flex items-end gap-4">
            <span className="text-4xl font-bold text-[#df1283]">{summary.click_rate || 0}%</span>
            <div className="flex items-center gap-1 text-xs text-[#df1283]">
              <ArrowUpRight className="w-4 h-4" />
              <span>Industry avg: 2.5%</span>
            </div>
          </div>
          <div className="mt-4 h-2 bg-[#4A0A66]/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#df1283] to-[#ff69b4] rounded-full transition-all duration-500"
              style={{ width: `${Math.min((summary.click_rate || 0) * 10, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-6">
          <h3 className="text-sm font-medium text-[#FDF2F8]/60 mb-4">Bounce Rate</h3>
          <div className="flex items-end gap-4">
            <span className="text-4xl font-bold text-[#F59E0B]">{summary.bounce_rate || 0}%</span>
            <div className="flex items-center gap-1 text-xs text-[#F59E0B]">
              {(summary.bounce_rate || 0) < 2 ? (
                <>
                  <ArrowDownRight className="w-4 h-4" />
                  <span>Healthy (&lt;2%)</span>
                </>
              ) : (
                <>
                  <ArrowUpRight className="w-4 h-4" />
                  <span>Needs attention</span>
                </>
              )}
            </div>
          </div>
          <div className="mt-4 h-2 bg-[#4A0A66]/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] rounded-full transition-all duration-500"
              style={{ width: `${Math.min((summary.bounce_rate || 0) * 20, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Daily Stats Chart */}
      {dailyStats.length > 0 && (
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Email Activity (Last 30 Days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#4A0A66" />
                <XAxis 
                  dataKey="date" 
                  stroke="#FDF2F8" 
                  tick={{ fill: '#FDF2F8', fontSize: 10 }}
                  tickFormatter={(value) => value.slice(5)}
                />
                <YAxis stroke="#FDF2F8" tick={{ fill: '#FDF2F8', fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#2D063E', 
                    border: '1px solid #4A0A66',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#FDF2F8' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="opens" 
                  stroke="#22C55E" 
                  strokeWidth={2}
                  dot={{ fill: '#22C55E', r: 3 }}
                  name="Opens"
                />
                <Line 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#df1283" 
                  strokeWidth={2}
                  dot={{ fill: '#df1283', r: 3 }}
                  name="Clicks"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Template Performance */}
      {templateStats.length > 0 && (
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Performance by Template</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#4A0A66]">
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#FDF2F8]/60">Template</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-[#FDF2F8]/60">Sent</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-[#FDF2F8]/60">Opens</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-[#FDF2F8]/60">Open Rate</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-[#FDF2F8]/60">Clicks</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-[#FDF2F8]/60">Click Rate</th>
                </tr>
              </thead>
              <tbody>
                {templateStats.map((template, idx) => (
                  <tr key={idx} className="border-b border-[#4A0A66]/30 hover:bg-[#4A0A66]/20">
                    <td className="py-3 px-4">
                      <span className="text-sm text-white">{template.name || template.template_type}</span>
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-[#FDF2F8]/80">
                      {template.sent?.toLocaleString() || 0}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-[#22C55E]">
                      {template.opens?.toLocaleString() || 0}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${
                        (template.open_rate || 0) > 25 ? 'text-[#22C55E]' : 
                        (template.open_rate || 0) > 15 ? 'text-[#F59E0B]' : 'text-red-400'
                      }`}>
                        {template.open_rate || 0}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-[#df1283]">
                      {template.clicks?.toLocaleString() || 0}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${
                        (template.click_rate || 0) > 5 ? 'text-[#22C55E]' : 
                        (template.click_rate || 0) > 2 ? 'text-[#F59E0B]' : 'text-red-400'
                      }`}>
                        {template.click_rate || 0}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Campaigns */}
      {campaigns.length > 0 && (
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Campaigns</h3>
          <div className="space-y-3">
            {campaigns.slice(0, 10).map((campaign, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-3 bg-[#4A0A66]/20 rounded-lg border border-[#4A0A66]/30"
              >
                <div>
                  <p className="text-sm font-medium text-white">{campaign.name || campaign.subject}</p>
                  <p className="text-xs text-[#FDF2F8]/50">
                    {campaign.sent_at ? new Date(campaign.sent_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="text-center">
                    <p className="text-[#FDF2F8]/60">Sent</p>
                    <p className="font-medium text-white">{campaign.sent_count || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[#FDF2F8]/60">Opens</p>
                    <p className="font-medium text-[#22C55E]">{campaign.opens || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[#FDF2F8]/60">Clicks</p>
                    <p className="font-medium text-[#df1283]">{campaign.clicks || 0}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!analytics?.summary?.total_sent && (
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-12 text-center">
          <Mail className="w-12 h-12 text-[#FDF2F8]/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Email Data Yet</h3>
          <p className="text-sm text-[#FDF2F8]/50 mb-4">
            Start sending emails to see analytics here. Use the nudge features or bulk email campaigns.
          </p>
          <Button 
            variant="outline" 
            className="border-[#df1283] text-[#df1283] hover:bg-[#df1283]/20"
          >
            Send Your First Campaign
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmailAnalyticsDashboard;
